import { useEqualisationSection } from '../EqualisationContext';
import EqualisationFooter from '../EqualisationFooter';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useParams, useHistory } from 'react-router-dom';
import FeesSummaryDisplay from 'components/FeesSummaryDisplay';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import PageSection from 'components/PageSection';
import TransactionAllocator from 'components/TransactionAllocator';
import { useState } from 'react';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { TransactionOperation } from 'further-types/lp/transaction';
import { ActionTransactionSubType } from 'further-types/lp/action';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import PageLoader from 'components/PageLoader';
import Spacing from 'components/Spacing';
import { EQUALISATIONS } from 'adminConstants/routes';
import { EqualisationSection } from 'further-types/lp/equalisation';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';

type Transaction = {
  transactionTypeId: string;
  amountIncVatTcy: number;
  amountExVatTcy: number;
  operationType: TransactionOperation;
  transactionSubType: ActionTransactionSubType;
  transactionCurrency: CurrencyCode;
};

const EqualisationSummary = () => {
  const history = useHistory();
  const { goToPreviousSection, previousSection } = useEqualisationSection();
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle, isLoading: isVehicleLoading } =
    useVehicle(selectedVehicleId);

  const listEqualisationsQueryKey = getQueryKey(api.equalisations.list, {
    vehicleId: vehicle?._id ?? '',
  });

  const listLpFeesQueryKey = getQueryKey(api.lpFee.list, {
    vehicleId: vehicle?._id ?? '',
  });

  const uncalledEqualisationsQueryKey = getQueryKey(
    api.capital.listUncalledEqualisations,
    {
      vehicleId: vehicle?._id ?? '',
    },
  );

  const vehicleQueryKey = getQueryKey(api.vehicle.getById, {
    vehicleId: vehicle?._id ?? '',
  });

  const commitmentsQueryKey = getQueryKey(api.commitments.list, {
    vehicleId: vehicle?._id ?? '',
  });

  const approveEqualisation = api.equalisations.approve.useMutation(
    useHandleTrpcMutation()({
      invalidations: [
        listEqualisationsQueryKey,
        listLpFeesQueryKey,
        uncalledEqualisationsQueryKey,
        commitmentsQueryKey,
        vehicleQueryKey,
      ],
    }),
  );
  const updateEqualisation = api.equalisations.update.useMutation();
  const [summaryDetails] = api.equalisations.getSummaryDetails.useSuspenseQuery(
    {
      equalisationId: id ?? '',
      vehicleId: selectedVehicleId ?? '',
    },
  );
  const { transactionTypeOptions, isLoading: transactionTypeOptionsLoading } =
    useTransactionTypeSelectorOptions();

  const title = ` management fees paid by [${vehicle?.vehicleName}] to the fund manager`;

  const [insideCommitmentTransactions, setInsideCommitmentTransactions] =
    useState<Array<Transaction>>(
      summaryDetails.insideCommitmentFees.length > 0
        ? summaryDetails.defaultTransactions.insideCommitment
        : [],
    );

  const [outsideCommitmentTransactions, setOutsideCommitmentTransactions] =
    useState<Array<Transaction>>(
      summaryDetails.outsideCommitmentFees.length > 0
        ? summaryDetails.defaultTransactions.outsideCommitment
        : [],
    );

  if (isVehicleLoading || transactionTypeOptionsLoading || !vehicle) {
    return <PageLoader />;
  }

  const fullFeeData = [
    ...summaryDetails.insideCommitmentFees,
    ...summaryDetails.outsideCommitmentFees,
  ].map((fee) => ({
    _id: fee._id,
    limitedPartner: fee.investorName ?? '',
    accrualDate: fee.accrualDate,
    lpFeeTemplateName: fee.feeTemplateName,
    lpFeeTemplateFeeName: fee.feeTemplateFeeName,
    insideOutsideCommitment: fee.commitmentStatus,
    feeExTaxLcy: fee.feeExTaxLcy,
    salesTaxAmountLcy: fee.salesTaxAmountLcy,
    feeIncTaxLcy: fee.feeIncTaxLcy,
    calculationBasis: fee.calculationBasis,
  }));

  const handleNext = () => {
    approveEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        feesInsideCommitmentTransactions: insideCommitmentTransactions,
        feesOutsideCommitmentTransactions: outsideCommitmentTransactions,
      },
      {
        onSuccess: () => {
          history.push(EQUALISATIONS);
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
      },
      {
        onSuccess: () => {
          goToPreviousSection();
        },
      },
    );
  };

  return (
    <Spacing>
      <FeesSummaryDisplay
        vehicle={{
          vehicleName: vehicle?.vehicleName ?? '',
        }}
        feeData={fullFeeData}
        feesDate={summaryDetails.date}
        title="Equalisation Summary for fees"
        showSummarySection
      />
      {summaryDetails.insideCommitmentFees.length > 0 && (
        <PageSection>
          <TransactionAllocator
            title={`Inside commitment` + title}
            vehicle={vehicle}
            transactions={insideCommitmentTransactions}
            transactionTypeOptions={transactionTypeOptions}
            transactionCurrency={vehicle.baseCurrency}
            fxRate={null}
            onChange={(transactions) => {
              setInsideCommitmentTransactions(transactions);
            }}
          />
        </PageSection>
      )}

      {summaryDetails.outsideCommitmentFees.length > 0 && (
        <PageSection>
          <TransactionAllocator
            title={`Outside commitment` + title}
            vehicle={vehicle}
            transactions={outsideCommitmentTransactions}
            transactionTypeOptions={transactionTypeOptions}
            transactionCurrency={vehicle.baseCurrency}
            fxRate={null}
            onChange={(transactions) => {
              setOutsideCommitmentTransactions(transactions);
            }}
          />
        </PageSection>
      )}
      <EqualisationFooter
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        disabled={false}
        loading={approveEqualisation.isPending || updateEqualisation.isPending}
      />
    </Spacing>
  );
};

export default EqualisationSummary;
